import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormRestaurantEditScheme } from "../../../../Models/Schemas/form-restaurants-edit";
import { FORM_RESTAURANT_EDIT } from "../../../../Constants/RestaurantEdit";
import Button from "../../../Elements/Button/Button";
import { setErrorFormSimpleUtil } from "../../../../Utilities/Forms/errors";
import useForm from "../../../../Hooks/useForm";
import Input from "../../../Elements/Input/Input";
import Loader from "../../../Elements/Loader/Loader";
import useStore from "../../../../useStore";
import { useToast } from "../../../../Contexts/Toast/toast.provider";
import { CATEGORIES } from "../../../../Json/categorias";

const EditMyRestaurant = () => {
    const { restauranteId } = useParams();
    const showToast = useToast();
    const navigate = useNavigate();
    const lastErrorRef = useRef(null);

    const {
        fetchDataGetRestaurantDetail,
        fetchDataEditMyRestaurant,
        restaurantDetail,
        loading,
        error,
        success,
    } = useStore();

    // useForm con valores iniciales y esquema Yup INVOCADO
    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        isValid,
    } = useForm(FORM_RESTAURANT_EDIT, FormRestaurantEditScheme());

    const [productos, setProductos] = useState([]);
    const [showAddProduct, setShowAddProduct] = useState(false);
    const [newProducto, setNewProducto] = useState({
        name: "",
        descripcion: "",
        precio: "",
        categoria: "",
        isAvailable: true,
        imageFile: null,
        imageUrl: "",
    });

    const [editIndex, setEditIndex] = useState(null);
    const [editProducto, setEditProducto] = useState({
        name: "",
        descripcion: "",
        precio: "",
        categoria: "",
        isAvailable: true,
        imageFile: null,
        imageUrl: "",
    });

    const lastSuccessRef = useRef(null);
    const initialSnapshotRef = useRef(null);

    const normalizeProducto = (p = {}) => ({
        name: p.name ?? "",
        descripcion: p.descripcion ?? "",
        precio: typeof p.precio === "number" ? p.precio : Number(p.precio || 0),
        categoria: p.categoria ?? "",
        isAvailable: !!(p.isAvailable ?? true),
        imageUrl: p.imageUrl ?? "",
    });

    const jsonEq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    const formCurrent = {
        name: values.name,
        categorias: values.categorias,
        phone: values.phone,
        email: values.email,
        horarios: values.horarios,
        tiempoEntregaEstimado: values.tiempoEntregaEstimado,
        costoEnvio: values.costoEnvio,
    };

    const productosCurrent = (productos || []).map(normalizeProducto);

    const formatPrecio = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(2) : "0.00";
    };

    const isFormDirty = (() => {
        if (!initialSnapshotRef.current) return false;
        return !jsonEq(formCurrent, initialSnapshotRef.current.form);
    })();

    const isProductosDirty = (() => {
        if (!initialSnapshotRef.current) return productosCurrent.length > 0;
        return !jsonEq(productosCurrent, initialSnapshotRef.current.productos);
    })();

    const NEW_PRODUCTO_DEFAULT = {
        name: "",
        descripcion: "",
        precio: "",
        categoria: "",
        isAvailable: true,
        imageFile: null,
        imageUrl: "",
    };

    const isNewProductoDirty = !jsonEq(
        { ...newProducto, imageFile: !!newProducto.imageFile }, // ignora la referencia del File
        { ...NEW_PRODUCTO_DEFAULT, imageFile: false }
    );

    const isEditProductoDirty = (() => {
        if (editIndex == null) return false;
        const original = productos[editIndex] ? normalizeProducto(productos[editIndex]) : null;
        if (!original) return false;
        const edited = normalizeProducto({ ...editProducto, precio: editProducto.precio });
        return !jsonEq(edited, original);
    })();

    const sanitizeInt = (s) => {
        if (s == null) return "";
        return String(s).replace(/\D/g, "");
    };

    const onProdChange = (key) => (arg) => {
        const raw = arg && arg.target !== undefined ? arg.target.value : arg ?? "";
        const value = key === "precio" ? sanitizeInt(raw) : raw;
        setNewProducto((prev) => ({ ...prev, [key]: value }));
    };

    const onEditChange = (key) => (arg) => {
        const raw = arg && arg.target !== undefined ? arg.target.value : arg ?? "";
        const value = key === "precio" ? sanitizeInt(raw) : raw;
        setEditProducto((prev) => ({ ...prev, [key]: value }));
    };

    const startEdit = (idx) => {
        const p = productos[idx] || {};
        setEditProducto({
            name: p.name ?? "",
            descripcion: p.descripcion ?? "",
            precio: p.precio ?? "",
            categoria: p.categoria ?? "",
            isAvailable: p.isAvailable ?? true,
            imageFile: null,
            imageUrl: p.imageUrl ?? "",
        });
        setEditIndex(idx);
    };

    const cancelEdit = () => {
        setEditIndex(null);
        setEditProducto({
            name: "",
            descripcion: "",
            precio: "",
            categoria: "",
            isAvailable: true,
            imageFile: null,
            imageUrl: "",
        });
    };

    const onEditToggleAvailable = (e) => {
        setEditProducto((prev) => ({ ...prev, isAvailable: e.target.checked }));
    };

    const saveEdit = () => {
        if (!editProducto.name || editProducto.precio === "") {
            showToast("error", "El producto necesita nombre y precio");
            return;
        }
        const precioNumber = Number(editProducto.precio);
        if (!Number.isFinite(precioNumber) || precioNumber < 0) {
            showToast("error", "El precio debe ser válido");
            return;
        }

        setProductos((prev) =>
            prev.map((p, i) =>
                i === editIndex
                    ? {
                        ...p,
                        name: editProducto.name.trim(),
                        descripcion: editProducto.descripcion.trim(),
                        precio: precioNumber,
                        categoria: editProducto.categoria || "",
                        isAvailable: !!editProducto.isAvailable,
                        imageUrl: editProducto.imageUrl,
                    }
                    : p
            )
        );

        showToast("success", "Producto actualizado ✅");
        cancelEdit();
    };

    // 1️⃣ Cargar detalle del restaurante al montar
    useEffect(() => {
        initialSnapshotRef.current = null; // nuevo restaurante, nuevo snapshot
        if (restauranteId) fetchDataGetRestaurantDetail(restauranteId);
    }, [restauranteId, fetchDataGetRestaurantDetail]);

    // 2️⃣ Cuando cargue el detalle, setear valores en el form
    useEffect(() => {
        if (restaurantDetail) {
            Object.keys(FORM_RESTAURANT_EDIT).forEach((key) => {
                if (restaurantDetail[key] !== undefined) {
                    handleChange(key)({
                        target: { name: key, value: restaurantDetail[key] },
                    });
                }
            });
            setProductos(restaurantDetail.productos || []);
        }
    }, [restaurantDetail, handleChange]);

    // 3️⃣ Éxito en edición
    useEffect(() => {
        if (!success || success === lastSuccessRef.current) return;
        lastSuccessRef.current = success;

        showToast("success", success.message || "Restaurante actualizado 🎉");
        navigate("/restaurants/get-my-restaurants", { replace: true });
        useStore.getState().resetSuccess();
    }, [success, navigate, showToast]);

    // 4️⃣ Manejo de errores
    useEffect(() => {
        if (!error || error === lastErrorRef.current) return;
        lastErrorRef.current = error;

        const msg =
            typeof error === "string" ? error : error.message || "Ocurrió un error";
        showToast("error", msg);
    }, [error, showToast]);

    useEffect(() => {
        if (restaurantDetail && !initialSnapshotRef.current) {
            // normaliza form inicial
            const initialForm = {
                name: restaurantDetail.name ?? "",
                categoriaPrincipal: restaurantDetail.categoriaPrincipal ?? "", // 👈 nuevo
                categorias: restaurantDetail.categorias ?? [],
                phone: restaurantDetail.phone ?? "",
                email: restaurantDetail.email ?? "",
                horarios: restaurantDetail.horarios ?? {},
                tiempoEntregaEstimado: restaurantDetail.tiempoEntregaEstimado ?? 30,
                costoEnvio: restaurantDetail.costoEnvio ?? 35,
            };
            // normaliza productos iniciales
            const initialProductos = (restaurantDetail.productos || []).map(normalizeProducto);

            initialSnapshotRef.current = {
                form: initialForm,
                productos: initialProductos,
            };
        }
    }, [restaurantDetail]);

    const onProdToggleAvailable = (e) => {
        setNewProducto((prev) => ({ ...prev, isAvailable: e.target.checked }));
    };

    const addProducto = () => {
        if (!newProducto.name || newProducto.precio === "") {
            showToast("error", "El producto necesita nombre y precio");
            return;
        }

        const precioNumber = Number(newProducto.precio);
        if (!Number.isFinite(precioNumber) || precioNumber < 0) {
            showToast("error", "El precio debe ser válido");
            return;
        }

        setProductos((prev) => [
            ...prev,
            {
                name: newProducto.name.trim(),
                descripcion: newProducto.descripcion.trim(),
                precio: precioNumber,
                categoria: newProducto.categoria || "",
                isAvailable: !!newProducto.isAvailable,
                imageUrl: newProducto.imageUrl,
            },
        ]);

        setNewProducto({ name: "", descripcion: "", precio: "", categoria: "", isAvailable: true, imageFile: null, imageUrl: "" });
        setShowAddProduct(false);
    };

    const removeProducto = (idx) => {
        setProductos((prev) => prev.filter((_, i) => i !== idx));
    };

    // Guardar cambios
    const submit = async (e) => {
        e.preventDefault();

        if (!isValid) {
            showToast("error", "Hay errores en el formulario");
            return;
        }

        const payload = {
            categoriaPrincipal: values.categoriaPrincipal, // 👈 nuevo
            categorias: values.categorias,
            email: values.email,
            horarios: values.horarios,
            productos,
            tiempoEntregaEstimado: values.tiempoEntregaEstimado,
            // name, phone y costoEnvio no se envían: son fijos desde la creación,
            // no editables por el momento (solo el admin los puede cambiar).
        };

        await fetchDataEditMyRestaurant(restauranteId, payload);
    };

    if (loading && !restaurantDetail) return <Loader />;

    const isDirtyOverall = isFormDirty || isProductosDirty || isNewProductoDirty || isEditProductoDirty;

    return (
        <div className="container-fluid restaurant-wrapper flex justify-center align-center">
            <div className="form-restaurant flex justify-center">
                <div className="restaurant-header">
                    <h4>Editar Restaurante</h4>
                    <p className="restaurant-subtext">
                        Modifica los datos de tu restaurante 🍴
                    </p>
                </div>

                <form onSubmit={submit}>
                    {/* Nombre — fijo desde la creación, no editable por el momento */}
                    <Input
                        value={values.name}
                        label="Nombre del restaurante (no editable, contacta al admin para cambiarlo)"
                        onChange={() => { }}
                        disabled
                    />

                    {/* Teléfono — fijo desde la creación, no editable por el momento */}
                    <Input
                        value={values.phone}
                        label="Teléfono (no editable, contacta al admin para cambiarlo)"
                        onChange={() => { }}
                        disabled
                    />

                    {/* Correo */}
                    <Input
                        value={values.email}
                        label="Correo electrónico"
                        onChange={handleChange("email")}
                        onBlur={handleBlur("email")}
                        error={setErrorFormSimpleUtil(errors, touched, "email")}
                        success={!setErrorFormSimpleUtil(errors, touched, "email")}
                        type="email"
                    />

                    {/* Categorías */}
                    {/* Categoría principal */}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Categoría principal
                        </label>
                        <select
                            value={values.categoriaPrincipal}
                            onChange={(e) => {
                                const cat = e.target.value;
                                handleChange("categoriaPrincipal")({ target: { name: "categoriaPrincipal", value: cat } });
                                // reset subcategorías al cambiar la principal
                                handleChange("categorias")({ target: { name: "categorias", value: [] } });
                            }}
                            className="border rounded-md p-2 w-full"
                        >
                            <option value="">-- Selecciona una categoría --</option>
                            {CATEGORIES.map((cat) => (
                                <option key={cat.name} value={cat.name}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Subcategorías */}
                    {values.categoriaPrincipal && (
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Subcategorías de {values.categoriaPrincipal}
                            </label>
                            <div className="flex flex-col gap-2 border rounded-md p-3 cats-grid">
                                {CATEGORIES.find((c) => c.name === values.categoriaPrincipal)?.subcategories.map((sub) => (
                                    <label key={sub} className="flex items-center gap-2 cat-item">
                                        <input
                                            type="checkbox"
                                            checked={(values.categorias || []).includes(sub)}
                                            onChange={(e) => {
                                                let newCats = [...(values.categorias || [])];
                                                if (e.target.checked) newCats.push(sub);
                                                else newCats = newCats.filter((c) => c !== sub);
                                                handleChange("categorias")({
                                                    target: { name: "categorias", value: newCats },
                                                });
                                            }}
                                        />
                                        <span>{sub}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Horarios */}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Horarios de atención
                        </label>

                        <div className="flex flex-col gap-2 border rounded-md p-3">
                            {[
                                "Lunes",
                                "Martes",
                                "Miércoles",
                                "Jueves",
                                "Viernes",
                                "Sábado",
                                "Domingo",
                            ].map((day) => {
                                const abierto = values.horarios?.[day]?.open || false;
                                const apertura = values.horarios?.[day]?.apertura || "";
                                const cierre = values.horarios?.[day]?.cierre || "";

                                return (
                                    <div
                                        key={day}
                                        className="grid grid-cols-[auto,1fr,1fr,1fr] items-center gap-3"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={abierto}
                                            onChange={(e) => {
                                                const newHorarios = { ...(values.horarios || {}) };
                                                newHorarios[day] = e.target.checked
                                                    ? { open: true, apertura: "09:00", cierre: "18:00" }
                                                    : { open: false, apertura: "", cierre: "" };
                                                handleChange("horarios")({
                                                    target: { name: "horarios", value: newHorarios },
                                                });
                                            }}
                                        />
                                        <span className="font-medium text-gray-700">{day}</span>

                                        <input
                                            type="time"
                                            className="border rounded-md px-2 py-1 disabled:bg-gray-100"
                                            value={apertura}
                                            disabled={!abierto}
                                            onChange={(e) => {
                                                const newHorarios = { ...(values.horarios || {}) };
                                                newHorarios[day] = {
                                                    ...(newHorarios[day] || {}),
                                                    apertura: e.target.value,
                                                };
                                                handleChange("horarios")({
                                                    target: { name: "horarios", value: newHorarios },
                                                });
                                            }}
                                        />

                                        <input
                                            type="time"
                                            className="border rounded-md px-2 py-1 disabled:bg-gray-100"
                                            value={cierre}
                                            disabled={!abierto}
                                            onChange={(e) => {
                                                const newHorarios = { ...(values.horarios || {}) };
                                                newHorarios[day] = {
                                                    ...(newHorarios[day] || {}),
                                                    cierre: e.target.value,
                                                };
                                                handleChange("horarios")({
                                                    target: { name: "horarios", value: newHorarios },
                                                });
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tiempo estimado */}
                    <Input
                        value={values.tiempoEntregaEstimado}
                        label="Tiempo estimado de entrega (min)"
                        onChange={handleChange("tiempoEntregaEstimado")}
                        onBlur={handleBlur("tiempoEntregaEstimado")}
                        error={setErrorFormSimpleUtil(errors, touched, "tiempoEntregaEstimado")}
                        success={!setErrorFormSimpleUtil(errors, touched, "tiempoEntregaEstimado")}
                        type="number"
                        min={10}
                        max={120}
                    />

                    {/* Costo de envío — fijo por ahora, no editable */}
                    <Input
                        value={values.costoEnvio}
                        label="Costo de envío (fijo, no editable por el momento)"
                        onChange={() => { }}
                        type="number"
                        disabled
                    />

                    {/* ---- Productos ---- */}
                    <center>
                        <Button
                            text="Agregar producto"
                            color="blue"
                            fill
                            action={() => setShowAddProduct(true)}
                            type="button"
                        />
                    </center>

                    {showAddProduct && (
                        <div className="mt-3 p-3 rounded-lg border">
                            <Input
                                label="Nombre del producto"
                                value={newProducto.name}
                                onChange={onProdChange("name")}
                            />
                            <Input
                                label="Descripción"
                                value={newProducto.descripcion}
                                onChange={onProdChange("descripcion")}
                            />
                            {/* Crear producto */}
                            <Input
                                label="Precio"
                                type="text"
                                inputMode="numeric"
                                pattern="\d*"
                                placeholder="0"
                                value={newProducto.precio}
                                onChange={onProdChange("precio")}
                            />

                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Categoría del producto
                                </label>
                                <select
                                    value={newProducto.categoria}
                                    onChange={(e) => setNewProducto(prev => ({ ...prev, categoria: e.target.value }))}
                                    className="border rounded-md p-2 w-full"
                                >
                                    <option value="">-- Sin categoría --</option>
                                    {["Comida", "Bebida", "Postre", "Antojo", "Otro"].map(cat => (
                                        <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Imagen */}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    try {
                                        const imageUrl = await useStore.getState().fetchDataUploadProductImage({
                                            restauranteId,
                                            productName: newProducto.name || file.name,
                                            file,
                                        });
                                        setNewProducto((prev) => ({ ...prev, imageFile: file, imageUrl }));
                                        showToast("success", "Imagen subida correctamente ✅");
                                    } catch {
                                        showToast("error", "Error subiendo imagen");
                                    }
                                }}
                            />
                            {newProducto.imageUrl && (
                                <img src={newProducto.imageUrl} alt="preview" className="mt-2 w-32 h-32 object-cover border" />
                            )}

                            <label className="flex items-center gap-2 mt-2">
                                <input
                                    type="checkbox"
                                    checked={!!newProducto.isAvailable}
                                    onChange={onProdToggleAvailable}
                                />
                                <span>Disponible</span>
                            </label>

                            <div className="mt-3 flex gap-2 justify-center">
                                <Button text="Guardar producto" color="blue" fill action={addProducto} />
                                <Button text="Cancelar" color="gray" fill action={() => setShowAddProduct(false)} />
                            </div>
                        </div>
                    )}

                    {productos.length > 0 && (
                        <div className="mt-3">
                            <h5>Productos agregados:</h5>
                            <ul>
                                {productos.map((p, idx) => (
                                    <li key={idx} className="flex justify-between items-start gap-3 py-2">
                                        {/* Si está en edición, muestra inputs; si no, el display normal */}
                                        {editIndex === idx ? (
                                            <div className="flex-1">
                                                <Input
                                                    label="Nombre del producto"
                                                    value={editProducto.name}
                                                    onChange={onEditChange("name")}
                                                />
                                                <Input
                                                    label="Descripción"
                                                    value={editProducto.descripcion}
                                                    onChange={onEditChange("descripcion")}
                                                />
                                                <Input
                                                    label="Precio"
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="\d*"
                                                    placeholder="0"
                                                    value={editProducto.precio}
                                                    onChange={onEditChange("precio")}
                                                />

                                                <div className="mb-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Categoría del producto
                                                    </label>
                                                    <select
                                                        value={editProducto.categoria}
                                                        onChange={(e) => setEditProducto(prev => ({ ...prev, categoria: e.target.value }))}
                                                        className="border rounded-md p-2 w-full"
                                                    >
                                                        <option value="">-- Sin categoría --</option>
                                                        {["Comida", "Bebida", "Postre", "Antojo", "Otro"].map(cat => (
                                                            <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Cambiar imagen en edición */}
                                                <div className="mt-2">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            try {
                                                                const imageUrl =
                                                                    await useStore.getState().fetchDataUploadProductImage({
                                                                        restauranteId,
                                                                        productName: editProducto.name || file.name,
                                                                        file,
                                                                    });
                                                                setEditProducto((prev) => ({ ...prev, imageFile: file, imageUrl }));
                                                                showToast("success", "Imagen actualizada ✅");
                                                            } catch {
                                                                showToast("error", "Error subiendo imagen");
                                                            }
                                                        }}
                                                    />
                                                    {editProducto.imageUrl && (
                                                        <div
                                                            className="mt-2 rounded border border-gray-300"
                                                            style={{
                                                                backgroundImage: `url(${editProducto.imageUrl})`,
                                                                backgroundSize: "contain",
                                                                backgroundRepeat: "no-repeat",
                                                                backgroundPosition: "center",
                                                                width: "100px",
                                                                height: "100px",
                                                            }}
                                                        />
                                                    )}
                                                </div>

                                                <label className="flex items-center gap-2 mt-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!editProducto.isAvailable}
                                                        onChange={onEditToggleAvailable}
                                                    />
                                                    <span>Disponible</span>
                                                </label>

                                                <div className="mt-3 flex gap-2">
                                                    <Button text="Guardar cambios" color="green" fill action={saveEdit} />
                                                    <Button text="Cancelar" color="gray" fill action={cancelEdit} />
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <span>
                                                    <strong>{p.name}</strong> — ${formatPrecio(p.precio)} {p.isAvailable ? "✅" : "⛔"}
                                                    {p.categoria && <span style={{ marginLeft: 6, fontSize: 12, color: "#666" }}>({p.categoria})</span>}
                                                    {p.imageUrl && <img src={p.imageUrl} alt={p.name} style={{ width: 50, marginLeft: 10 }} />}
                                                </span>
                                                <div className="flex gap-2">
                                                    <Button text="Editar" color="blue" action={() => startEdit(idx)} />
                                                    <Button text="Quitar" color="red" action={() => removeProducto(idx)} />
                                                </div>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Botón Guardar */}
                    <center>
                        <Button
                            text="Guardar Cambios"
                            color="green"
                            fill
                            disabled={loading || !isValid || !isDirtyOverall}
                            classNames="mt-3"
                            type="submit"
                        />
                    </center>
                </form>
            </div>
        </div>
    );
};

export default EditMyRestaurant;
